<template>
  <div>
    <v-card class="pa-10" elevation="0">
      <v-row>
        <v-col>
          <v-row no-gutters align="center">
            <h4 class="greyDark--text font-weight-bold mb-0">
              {{ $t('common:documentation_plural') }}
            </h4>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs}">
                <v-btn class="ml-2" icon @click="cardView = !cardView" v-bind="attrs" v-on="on">
                  <v-icon>list_alt</v-icon>
                </v-btn>
              </template>
              <span v-if="cardView">{{ $t('buttons:table-view') }}</span>
              <span v-else>{{ $t('buttons:card-view') }}</span>
            </v-tooltip>
          </v-row>
        </v-col>
        <v-col class="d-none d-md-block py-0">
          <RadialScore ref="roleScore" :score="score" score-icon="document"/>
        </v-col>
        <v-col class="text-right">
          <BaseButton
              color="primary"
              :btnText="$t('buttons:add-specific', { specific: $t('common:documentation') })"
              @clickedButton="openFormDialog"
          />
        </v-col>
        <v-col cols="12" sm="12" class="d-block d-md-none">
          <RadialScore ref="roleScore" :score="score" score-icon="document"/>
        </v-col>
      </v-row>

      <!-- Docu toggle button to only show specific types -->
      <!-- toggle buttons need also to be visible on table view -->
      <v-row class="my-0">
        <v-col class="mt-0 py-0">
          <v-btn-toggle v-model="docuToggle" class="mt-0">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs}">
                <v-btn
                    value="ALL"
                    icon
                    v-bind="attrs" v-on="on"
                    color="greyMedium"
                >
                  <v-icon color="greyMedium">select_all</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('book/facility:facility.dashboard.information.docus.filter-btns.all') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs}">
                <v-btn
                    value="DOCUMENTS"
                    icon
                    v-bind="attrs" v-on="on"
                    color="greyMedium"
                >
                  <v-icon color="greyMedium">description</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('book/facility:facility.dashboard.information.docus.filter-btns.documents') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs}">
                <v-btn
                    value="STATE_DOCUMENTS"
                    icon
                    v-bind="attrs" v-on="on"
                    color="greyMedium"
                >
                  <v-icon color="greyMedium">insights</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('book/facility:facility.dashboard.information.docus.filter-btns.state-documents') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs}">
                <v-btn
                    value="IMAGES"
                    icon
                    v-bind="attrs" v-on="on"
                    color="greyMedium"
                >
                  <v-icon color="greyMedium">image</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('book/facility:facility.dashboard.information.docus.filter-btns.images') }}</span>
            </v-tooltip>

            <v-tooltip bottom v-if="this.showContracts">
              <template v-slot:activator="{ on, attrs}">
                <v-btn
                    value="CONTRACTS"
                    icon
                    v-bind="attrs" v-on="on"
                    color="greyMedium"
                >
                  <v-icon color="greyMedium">gavel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('book/facility:facility.dashboard.information.docus.filter-btns.contracts') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs}">
                <v-btn
                    value="GENERATED"
                    icon
                    v-bind="attrs" v-on="on"
                    color="greyMedium"
                >
                  <v-icon color="greyMedium">loop</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('book/facility:facility.dashboard.information.docus.filter-btns.sproof') }}</span>
            </v-tooltip>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-row v-if="!cardView">
        <v-col cols="12" class="pt-0" >
          <DataTablePaginated
            ref="tableDocuments"
            :headers="docuHeaders"
            @updateTableData="updateDocuTable"
            icon="visibility"
            icon2="cloud_download"
            icon3="delete"
            :iconTooltip="$t('buttons:show')"
            :iconTooltip2="$t('buttons:download')"
            :iconTooltip3="$t('buttons:delete')"
            @clickedButton1="viewDocumentation"
            @clickedButton2="downloadDocumentation"
            @clickedButton3="deleteDocumentation"
            sortBy="translatedType"
            @cacheCommit="$store.commit('book/facility/storeFacilityDocumentationsSearch', $event)"
            :cachedSearch="$store.state.book.facility.documentationsSearch"
        />
        </v-col>
    </v-row>
    <div v-if="cardView">

      <SearchAndSortBar
          @updateData="updateDocuTable"
          @cacheCommit="$store.commit('book/facility/storeFacilityDocumentationsSearch', $event)"
          :cachedSearch="$store.state.book.facility.documentationsSearch"
          class="mb-3"
          :sorts="docuHeaders"
          :filters="dataFilters"
          filterValueField="name"
          filterTextField="translatedName"
          default-sort-by="translatedType"
        />

      <v-row>
        <v-col v-show="docuToggle === 'ALL' || docuToggle === 'DOCUMENTS'" cols="12" md="6" :lg="cardSize" xl="4" v-for="item in requiredDocus" :key="item.id">
          <DragAndDropItemCard
              :title="item.translatedName"
              required
              height="400"
              @fileChange="addRequiredDocu($event, item)"
              :priority="item.priority"
          />
        </v-col>
        <v-col cols="12" md="6" :lg="cardSize" xl="4" v-for="item in restDocu.content" :key="item.id" >
          <DragAndDropItemCard
              :item="item"
              :title="item.translatedType"
              height="400"
              :was-required="item.wasRequired"
              @deleteItem="deleteDocumentation(item)"
              @downloadFile="downloadDocumentation(item)"
              @viewFile="viewDocumentation(item)"
              @editItem="viewDocumentation(item)"
              :viewIconTooltip="$t('buttons:show')"
              :downloadIconTooltip="$t('buttons:download')"
              :description="cutFileName(item.name)"
              :additionalInfo="$t('book/facility:facility.dashboard.information.docus.item-card.valid') + ': ' + getValidity(item)"
              :show-status="checkDocuStatus(item)"
          />
        </v-col>
        </v-row>
        <v-pagination v-show="restDocu.content.length !== 0"
          class="mt-3"
          v-model="restDocu.options.page"
          :length="totalPages"
          :total-visible="7">
        </v-pagination>
        </div>
        <v-col v-if="restDocu.content.length === 0 && restRequiredDocus.length === 0" class="text-center">
          <h6 class="greyTint--text mb-0">
            {{ $t('common:no-data-found') }}
          </h6>
        </v-col>
      <v-col v-else-if="docuToggle !== 'ALL' && docuToggle !== 'DOCUMENTS' && restDocu.content.length === 0" class="text-center">
        <h6 class="greyTint--text mb-0">
          {{ $t('common:no-data-found') }}
        </h6>
      </v-col>
    </v-card>

    <FacilityDocuDialogForm
        ref="docuForm"
        @updateDocuData="callUpdateTable"
    />

    <DocumentViewer ref="documentViewer"/>

    <TwoButtonDialog
        ref="deleteDocumentationButton"
        :btn-text="$t('buttons:delete')"
        color="grey"
        :dialog-title="$t('dialog:delete-btn-title',{name : $t('book/facility:facility.dashboard.information.docus.delete-dialog-part-1')})"
        :dialog-text="$t('dialog:delete-btn-dialog',{name : $t('book/facility:facility.dashboard.information.docus.delete-dialog-part-2')})"
        :button1-text="$t('buttons:cancel')"
        :button2-text="$t('buttons:delete')"
        @clickedButton1="$refs.deleteDocumentationButton.showDialog = false"
        @clickedButton2="deleteDocumentationConfirmed()"
        :hide-button="true"
    />

  </div>
</template>

<script>

import DocumentationService from '@/modules/core/services/documentation-service';

import Utils from '@/modules/core/services/utils';

import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog';
import DataTablePaginated from '@/modules/core/components/DataTable/DataTablePaginated';
import FacilityDocuDialogForm from '../formDialogs/FacilityDocuDialogForm'
import BaseButton from '@/modules/core/components/Base/BaseButton';
import SearchAndSortBar from '@/modules/core/components/SearchAndSortBar';

import {mapState} from 'vuex'
import FacilityTypeService from '@/modules/book/services/facility-type-service';
import DocumentationRule from '@/modules/book/data/documentation-rule';
import DataUtils from '@/modules/core/services/data-utils';
import DragAndDropItemCard from '@/modules/core/components/DragAndDropItemCard';
import Vue from 'vue';
import Score from '@/modules/book/data/score';
import RadialScore from '@/modules/core/components/RadialScore';
import InfoService from '@/modules/core/services/info-service';
import {DocumentType} from '@/modules/book/data/document-type.js';

export default {
  name: 'FacilityDocuInfoTab',
  components: {
    DragAndDropItemCard,
    TwoButtonDialog,
    DataTablePaginated,
    FacilityDocuDialogForm,
    DocumentViewer: () => import('@/modules/core/components/DocumentViewer/DocumentViewer'),
    BaseButton,
    SearchAndSortBar,
    RadialScore,
  },
  data() {
    return {
      restDocu: {
        options: [],
        search: [],
        filter: [],
        content: []
      },
      restRequiredDocus: [],
      cardView: true,
      totalPages: null,
      score: new Score(),
      dataFilters: [],
      docuToggle: 'ALL',
      deleteGbuReportRefs: false,
    }
  },
  computed: {
    docuHeaders() {
      return [
        {text: this.$t("common:type"), value: "translatedType"},
        {text: this.$t("common:document-name"), value: "name"},
        {text: this.$t('common:valid-from'), value: 'startDate'},
        {text: this.$t('common:valid-until'), value: 'endDate'},
        {text: "", value: "controls", width: "190px", sortable: false},
      ];
    },

    cardSize() {
      // if both sidebars are fully opened, make card size bigger
      if(this.leftFacilityDrawer && this.rightFacilityDrawer && this.$vuetify.breakpoint.xl) {
        return '6'
      } else if (this.leftFacilityDrawer && this.rightFacilityDrawer && this.$vuetify.breakpoint.lgAndDown) {
        return '12'
        // if both side bars are closed, make card size smaller
      } else if(!this.leftFacilityDrawer && !this.rightFacilityDrawer) {
        return '3'
      } else {
        return '4'
      }
    },

    ...mapState('book/layout', {
      leftFacilityDrawer: state => state.leftFacilityDrawer,
      rightFacilityDrawer: state => state.rightFacilityDrawer
    }),

    currentLang() {
      return this.$store.state.translation.language;
    },

    // return required documents if user is on page 1
    // so there wont be v-col problems when swithcing to a different page
    requiredDocus() {
      if (this.restDocu.options.page === 1) {
        return this.restRequiredDocus;
      } else {
        return []
      }
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        for (let i = 0; i < this.restDocu.content.length; i++) {
          this.restDocu.content[i].translatedType = this.$t('enums:facility-documentation-types.' + this.restDocu.content[i].type)
          Vue.set(this.restDocu.content, i, this.restDocu.content[i])
        }

        for (let i = 0; i < this.restRequiredDocus.length; i++) {
          this.restRequiredDocus[i].translatedName = this.$t('enums:facility-documentation-types.' + this.restRequiredDocus[i].name)
          Vue.set(this.restRequiredDocus, i, this.restRequiredDocus[i])
        }
      });
    },
    // when switching from tableView to cardView, refresh data
    cardView() {
      if (this.cardView) {
        this.updateDocuTable(this.restDocu.options, this.restDocu.search);
      }
    },

    // if docu toggle has changed, get the according filter and update the data with according filter
    docuToggle(val, oldVal) {
      // when double clicking on one toggle button, the value is undefined which can't be handled in the backend
      // if it is undefined, set it to to the docu category 'ALL'
      if (!val) {
        val = 'ALL';
        this.docuToggle = 'ALL';
      }

      this.getFilters();
      this.restDocu.filter = "";
      // reset page# to 1 when docuToggle changed
      if (oldVal !== val) {
        this.restDocu.options.page = 1;
      }
      this.updateDocuTable(this.restDocu.options, this.restDocu.search, this.restDocu.filter)
    }
  },
  methods: {
    checkDocuStatus(item) {
      if (item.generated || item.toBeSigned || item.signed) {
        return true;
      } else {
        return false;
      }
    },

    cutFileName(name) {
      return Utils.cropText(name, 28, false);
    },

    updateDocuTable(options, search, filter) {
      this.restDocu.options = options;
      this.restDocu.search = search;
      this.restDocu.filter = filter;

      if(!search) search = '';
      if (!filter) filter = '';
      if (!this.cardView) {
        this.$refs.tableDocuments.loading = true;
      }

      DocumentationService.readPageableFacilityDocumentationsFromAPI(
        this.$route.params.id,
        options.page - 1,
        options.itemsPerPage,
        search,
        filter,
        this.docuToggle,
        options.sortBy,
        options.sortDesc,
        (res) => {
          res.data.content.forEach((document) => {
            // set translated type for document
            document.translatedType = this.$t('enums:facility-documentation-types.' + document.type);
            document.src = '';

            if (document.type === 'POA' && !document.type.includes('.pdf')) {
              document.fileName = document.fileName + '.pdf';
            }
          })

          // we have to manually sort here, because we translate the enums in the frontend
          if (options.sortBy[0] === 'translatedType') {
            this.restDocu.content = this.sort(res.data.content, options.sortDesc, "translatedType");
          } else {
            this.restDocu.content = res.data.content;
          }

          if (!this.cardView) {
            this.$refs.tableDocuments.fillTableData(res.data);
          } else {
            this.loadPreviewForCollection();
            // if facilityType has required docus set
            this.getRequiredDocumentations()
            this.totalPages = res.data.totalPages;
          }
          this.getScore();

          // trigger getDocus in Facility.vue
          this.$emit('updateQuicklinkDocus')
        },
        (error) => {
          if (!this.cardView) {
            this.$refs.tableDocuments.loading = false;
          }
          this.$logger.info("Error at fetching facility documentations: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/facility:facility.snackbar.facility-documents')}))
        }
      )
    },
    loadPreviewForCollection() {
      if (this.restDocu.content.length > 0) {
        for (let i = 0; i < this.restDocu.content.length; i++) {

          // get file url to display a preview image of the document
          DocumentationService.getDocByTypeAndId(
              this.restDocu.content[i].id,
              this.restDocu.content[i].fileType,
              DocumentType.FACILITY,
              this.restDocu.content[i].facilityId,
              (result) => {
                this.restDocu.content[i].src = result;
              },
              (error) => {
                this.$logger.error("Failed to get documentation source, because: " + error);
              }
          )
        }
      }
    },
    downloadDocumentation(item) {
      DocumentationService.downloadFile(item.id, item.fileName, item.fileType, item.facilityId, item.documentType);
    },
    deleteDocumentation(item) {
      this.deleteDocumentationId = item.id;
      this.deleteGbuReportRefs = item.type === 'GBU_REPORT';
      this.$refs.deleteDocumentationButton.showDialog = true;
    },
    deleteDocumentationConfirmed() {
      DocumentationService.delete(this.deleteDocumentationId,
        () => {
          if (this.deleteGbuReportRefs) {
            this.$emit("deleteGbuReport",this.deleteDocumentationId,'test');
          }
          this.updateDocuTable(this.restDocu.options, this.restDocu.search);
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:documentation')}));
          this.$emit('deletedItem');
        },
        (error) => {
          this.$logger.error("Failed to delete user documentation, because: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:documentation')}));
        }
      )
      this.$refs.deleteDocumentationButton.showDialog = false;
    },

    viewDocumentation(item) {
      let search = '';
      let filter = this.restDocu.filter ? this.restDocu.filter : '';
      DocumentationService.readPageableFacilityDocumentationsFromAPI(
          this.$route.params.id,
          0,
          -1, // get all docs in one page
          search,
          filter,
          this.docuToggle,
          this.restDocu.options.sortBy,
          this.restDocu.options.sortDesc,
          (res) => {
            res.data.content.forEach((document) => {
              // set translated type for document
              document.translatedType = this.$t('enums:facility-documentation-types.' + document.type);
              document.src = '';

              if (document.type === 'POA' && !document.type.includes('.pdf')) {
                document.fileName = document.fileName + '.pdf';
              }
            })

            let allContent = res.data.content;
            // we have to manually sort here, because we translate the enums in the frontend
            if (this.restDocu.options.sortBy.length > 0 &&  this.restDocu.options.sortBy[0] === 'translatedType') {
              allContent = this.sort(res.data.content, this.restDocu.options.sortDesc, "translatedType");
            }

            this.$refs.documentViewer.initViewerById(allContent, item.id, item.facilityId, item.documentType);
          });
    },

    openFormDialog() {
      this.$refs.docuForm.addDocu(this.restDocu.options, this.restDocu.search)
    },

    getValidity(item) {
      if(item.startDate && item.endDate) {
        return item.startDate + ' - ' + item.endDate
      } else if(item.startDate && !item.endDate) {
        return this.$t('book/facility:facility.dashboard.information.docus.item-card.since') + ' ' + item.startDate
      } else if(!item.startDate && item.endDate) {
        return this.$t('book/facility:facility.dashboard.information.docus.item-card.till') + ' ' + item.endDate
      } else {
        return this.$t('book/facility:facility.dashboard.information.docus.item-card.valid-not-set')
      }
    },

    addRequiredDocu(file, item) {
      if (file) {
        this.$refs.docuForm.setFile(file)

        // set name of documentation automatic from the filename
        this.$refs.docuForm.documentation.name = file.name.split('.')[0];

        // set type of documentation automatically from the clicked req doc card
        this.$refs.docuForm.documentation.type = item.name;
        this.$refs.docuForm.addDocu(this.restDocu.options, this.restDocu.search);
      }
    },

    sort(data, sortDesc, sortBy) {
      if (!sortDesc[0]) {
        data.sort((a, b) => {
          if(a[sortBy] < b[sortBy]) {
            return -1;
          }
          if(a[sortBy] > b[sortBy]) {
            return 1;
          }
          return 0;
        })
      } else {
        data.sort((a, b) => {
          if(a[sortBy] > b[sortBy]) {
            return -1;
          }
          if(a[sortBy] < b[sortBy]) {
            return 1;
          }
          return 0;
        })
      }

      return data;
    },

    getRequiredDocumentations() {
      FacilityTypeService.readRequiredDocumentaitonsForFacilityFromAPI(this.$route.params.id,
          (res) => {
            this.restRequiredDocus = DataUtils.parseDataToObjectArray(res.data, DocumentationRule);
            for (let i = 0; i < this.restRequiredDocus.length; i++) {
              this.restRequiredDocus[i].translatedName = this.$t('enums:facility-documentation-types.' + this.restRequiredDocus[i].name);
            }
            this.sort(this.restRequiredDocus, true, 'priority');
          }
      )
    },

    getScore() {
      DocumentationService.readFacilityDocumentationScoreForFacility(this.$route.params.id,
          (res) => {
            this.score = DataUtils.parseDataToSingleObject(res.data, Score);
            this.$emit("updateDocuScore", this.score.percentage);
          }
      )
    },

    // get according filters depending which toggle button is selected
    getFilters() {
      if (this.docuToggle === 'ALL') {
        InfoService.readAndTranslateEnum(
            'documentation/facility/all',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      } else if (this.docuToggle === 'DOCUMENTS') {
        InfoService.readAndTranslateEnum(
            'documentation/facility',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      } else if (this.docuToggle === 'STATE_DOCUMENTS') {
        InfoService.readAndTranslateEnum(
            'documentation/facility/state',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      } else if (this.docuToggle === 'IMAGES') {
        InfoService.readAndTranslateEnum(
            'documentation/facility/image',
            "enums:facility-documentation-types.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      } else if (this.docuToggle === 'CONTRACTS') {
        InfoService.readAndTranslateEnum(
            "contract/type",
            "utils:enum.documentation.facility.",
            (res) => {
              this.dataFilters = Utils.sort(res, false, 'translatedName');
            }
        )
      }
    },
    callUpdateTable() {
      if (!this.cardView) {
        this.$refs.tableDocuments.callTableUpdate();
      } else {
        this.updateDocuTable(this.restDocu.options, this.restDocu.search, this.restDocu.filter);
      }
    }
  },
  props: {
    showContracts : Boolean
  },
  created() {
    this.getFilters()
  }
}
</script>